import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { toast } from 'react-toastify';

import { Icon } from 'assets/icons';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { useWordSearchService } from 'hooks/api/agents/content/word-search';
import { addOneWordSearchToStore, removeOneWordSearchFromStore } from 'store/actions/word-search';
import { downloadFile, toFileName } from 'utilities/file';
import { formatDateTime } from '../../jobs/config/job';
import {
  PENDING_STATUSES,
  STATUS_LABELS,
  WORD_SOURCE_LABELS,
  resolvePlacementCells,
  resolveWordSearchTitle
} from '../config/word-search';
import styles from './view.module.css';

const POLL_INTERVAL = 4000;

export const WordSearchViewPage = ({ word_search_id, onDelete = () => {} }) => {
  const dispatch = useDispatch();
  const { deleteWordSearch, fetchWordSearch } = useWordSearchService();
  const word_search = useSelector((state) => state.word_searches[word_search_id]);

  const [show_answers, setShowAnswers] = useState(false);
  const [hovered_word, setHoveredWord] = useState(null);
  const [show_delete_dialog, setShowDeleteDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const is_pending = PENDING_STATUSES.includes(word_search?.status);

  useEffect(() => {
    setShowAnswers(false);
    setHoveredWord(null);
    handleDataRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word_search_id]);

  useEffect(() => {
    if (!is_pending) return;
    const interval = setInterval(handleDataRequest, POLL_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word_search_id, is_pending]);

  const placements_by_word = useMemo(
    () =>
      (word_search?.placements || []).reduce(
        (acc, placement) => ({ ...acc, [placement.word]: resolvePlacementCells(placement) }),
        {}
      ),
    [word_search?.placements]
  );

  const highlighted_cells = useMemo(() => {
    if (hovered_word) return new Set(placements_by_word[hovered_word] || []);
    if (show_answers) return new Set(Object.values(placements_by_word).flat());
    return new Set();
  }, [hovered_word, show_answers, placements_by_word]);

  const handleDataRequest = async () => {
    const { word_search } = await fetchWordSearch(word_search_id);
    if (word_search) dispatch(addOneWordSearchToStore(word_search));
  };

  const handleImageDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        word_search.image_url,
        toFileName(resolveWordSearchTitle(word_search), 'png')
      );
    } catch (e) {
      toast.error('Unable to download image at this time.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteConfirmation = async (permitted) => {
    setShowDeleteDialog(false);
    if (!permitted) return;

    const deleted = await deleteWordSearch(word_search_id);
    if (!deleted) return;

    dispatch(removeOneWordSearchFromStore(word_search_id));
    onDelete();
  };

  if (!word_search) {
    return (
      <div className={styles.pendingState}>
        <span className={styles.pendingSpinner} />
        <p className={styles.pendingTitle}>Loading word search…</p>
      </div>
    );
  }

  const { status, error, words = [], grid = [], image_url, border_color } = word_search;
  const title = resolveWordSearchTitle(word_search);
  const words_source =
    word_search.words_source || (word_search.input_words?.length ? 'user' : null);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.meta}>
            <span className={cx(styles.statusBadge, styles[status])}>
              {STATUS_LABELS[status] || status}
            </span>
            <span>{word_search.word_count} words</span>
            {words_source && <span>{WORD_SOURCE_LABELS[words_source] || words_source}</span>}
            <span>
              {word_search.grid_size
                ? `${word_search.grid_size}×${word_search.grid_size}`
                : grid.length
                ? `${grid.length}×${grid.length} (auto)`
                : 'Auto size'}
            </span>
            <span className={styles.colorChip}>
              <span className={styles.colorSwatch} style={{ background: border_color }} />
              {border_color}
            </span>
            <span>{formatDateTime(word_search.created_on)}</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          {image_url && (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={handleImageDownload}
              disabled={downloading}
            >
              <Icon name="download" width={15} height={15} />
              <span>{downloading ? 'Downloading…' : 'Image'}</span>
            </button>
          )}
          <button
            type="button"
            className={cx(styles.actionBtn, styles.dangerBtn)}
            onClick={() => setShowDeleteDialog(true)}
            disabled={is_pending}
            title={is_pending ? 'Wait for generation to finish' : 'Delete word search'}
          >
            <Icon name="trash" width={15} height={15} />
          </button>
        </div>
      </div>

      {is_pending && (
        <div className={styles.pendingState}>
          <span className={styles.pendingSpinner} />
          <p className={styles.pendingTitle}>
            {status === 'queued' ? 'Queued for generation' : 'Generating puzzle…'}
          </p>
          <p className={styles.pendingHint}>
            Picking words, placing them on the grid and rendering the image. This page updates
            automatically.
          </p>
        </div>
      )}

      {status === 'failed' && (
        <div className={styles.errorBox}>
          <Icon name="warning" width={16} height={16} />
          <span>{error || 'Generation failed.'}</span>
        </div>
      )}

      {status === 'completed' && (
        <div className={styles.body}>
          <div className={styles.imagePane}>
            {image_url ? (
              <img className={styles.image} src={image_url} alt={`Word search: ${title}`} />
            ) : (
              <p className={styles.pendingHint}>No image was uploaded for this puzzle.</p>
            )}
          </div>

          <div className={styles.answerPane}>
            <div className={styles.answerHeader}>
              <h3 className={styles.sectionTitle}>Answer key</h3>
              <button
                type="button"
                className={styles.toggleBtn}
                onClick={() => setShowAnswers((shown) => !shown)}
              >
                <Icon name={show_answers ? 'hide' : 'show'} width={14} height={14} />
                <span>{show_answers ? 'Hide answers' : 'Show answers'}</span>
              </button>
            </div>

            {grid.length > 0 && (
              <div
                className={styles.answerGrid}
                style={{ gridTemplateColumns: `repeat(${grid.length}, 1fr)` }}
              >
                {grid.map((row, row_index) =>
                  Array.from(row).map((letter, col_index) => (
                    <span
                      key={`${row_index}:${col_index}`}
                      className={cx(
                        styles.answerCell,
                        highlighted_cells.has(`${row_index}:${col_index}`) && styles.answerCellHit
                      )}
                    >
                      {letter}
                    </span>
                  ))
                )}
              </div>
            )}

            <ul className={styles.wordList}>
              {words.map((word) => (
                <li
                  key={word}
                  className={cx(styles.wordItem, hovered_word === word && styles.wordItemActive)}
                  onMouseEnter={() => setHoveredWord(word)}
                  onMouseLeave={() => setHoveredWord(null)}
                >
                  {word}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <ConfirmationDialog
        message="This permanently deletes the word search and its answer key. Continue?"
        is_open={show_delete_dialog}
        callback={handleDeleteConfirmation}
      />
    </div>
  );
};
